import { useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";
export interface Preferences {
  theme: ThemeMode;
  highContrast: boolean;
}

const STORAGE_KEY = "station.preferences";
const listeners = new Set<() => void>();

function load(): Preferences {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as Partial<Preferences>;
    return { theme: stored.theme ?? "system", highContrast: stored.highContrast ?? false };
  } catch {
    return { theme: "system", highContrast: false };
  }
}

let preferences: Preferences =
  typeof localStorage === "undefined" ? { theme: "system", highContrast: false } : load();

export function resolveTheme(mode: ThemeMode, prefersDark: boolean): "light" | "dark" {
  return mode === "system" ? (prefersDark ? "dark" : "light") : mode;
}

/** Tema ve kontrast tercihini `<html>` üzerine uygular; token'lar bu özniteliklere göre değişir. */
export function applyPreferences(): void {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const prefersContrast = window.matchMedia("(prefers-contrast: more)").matches;
  document.documentElement.dataset.theme = resolveTheme(preferences.theme, prefersDark);
  document.documentElement.dataset.contrast = preferences.highContrast || prefersContrast ? "high" : "normal";
}

export function setPreferences(patch: Partial<Preferences>): void {
  preferences = { ...preferences, ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  applyPreferences();
  listeners.forEach((listener) => listener());
}

export function usePreferences(): Preferences {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => preferences,
  );
}

export function watchSystemPreferences(): () => void {
  const queries = [
    window.matchMedia("(prefers-color-scheme: dark)"),
    window.matchMedia("(prefers-contrast: more)"),
  ];
  queries.forEach((query) => query.addEventListener("change", applyPreferences));
  return () => queries.forEach((query) => query.removeEventListener("change", applyPreferences));
}
